import { useState, useEffect, useRef } from 'react';
import AnimatedSection from '../../components/AnimatedSection';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import logo from '../../assets/logo.png';
import useNaverMaps from '../../hooks/useNaverMaps';

// 지도 렌더링을 담당하는 자식 컴포넌트
const NaverMapComponent = ({ address }) => {
  const mapElement = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    // 이 컴포넌트는 isReady가 true일 때만 렌더링되므로, naver 객체는 항상 사용 가능합니다.
    const { naver } = window;
    if (!mapElement.current || !naver) return;

    // 지도가 이미 생성되었다면 다시 생성하지 않음
    if (mapInstance.current) return;

     // 주소를 좌표로 변환하는 Geocoding 요청
    naver.maps.Service.geocode({ query: address }, (status, response) => {
      if (status !== naver.maps.Service.Status.OK || !response.v2.addresses || response.v2.addresses.length === 0) {
        if (mapElement.current) {
            mapElement.current.innerHTML = `<div class="text-red-500 p-4">주소 좌표를 찾을 수 없습니다: ${address}</div>`;
        }
        return console.error('Geocoding failed:', status, response);
      }

      const result = response.v2.addresses[0];
      const coords = new naver.maps.LatLng(result.y, result.x);

      // 지도 옵션 설정
      const mapOptions = {
        center: coords,
        zoom: 17,
        zoomControl: true,
        zoomControlOptions: {
          style: naver.maps.ZoomControlStyle.SMALL,
          position: naver.maps.Position.TOP_RIGHT,
        },
        mapDataControl: false,
      };

      // 지도 생성 및 ref에 인스턴스 저장
      const map = new naver.maps.Map(mapElement.current, mapOptions);
      mapInstance.current = map;
      
      const marker = new naver.maps.Marker({
          position: coords,
          map: map,
          title: "진양건설"
        });

        // 정보 창(InfoWindow) 생성 및 표시
        const infoWindow = new naver.maps.InfoWindow({
            content: '<div style="padding:10px; font-weight:bold; font-size:14px; text-align:center; border: 1px solid #ccc; background-color: white; border-radius: 5px;">진양건설</div>',
            // 테두리와 배경을 추가하여 더 잘 보이도록 함
            disableAutoPan: true, // 정보 창이 열릴 때 지도가 자동으로 움직이는 것을 방지
            borderWidth: 0, // 기본 테두리 제거
            backgroundColor: 'transparent', // 기본 배경 제거
            pixelOffset: new naver.maps.Point(0, -15) // 마커 위로 살짝 올림
        });

        // 페이지 로드 시 정보 창이 기본적으로 열려 있도록 함
        infoWindow.open(map, marker);

        mapInstance.current = map;
    });
  }, [address]);

  return (
    <div
      ref={mapElement}
      style={{ width: '100%', height: '450px' }}
      className="rounded-lg overflow-hidden shadow-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500"
    >
    </div>
  );
};


// 전화번호에 자동으로 하이픈(-)을 추가해주는 포맷터 함수
const formatPhoneNumber = (value) => {
  if (!value) return value;
  // 숫자만 추출
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  
  // 서울 지역번호(02) 처리
  if (phoneNumber.startsWith('02')) {
    if (phoneNumberLength < 3) return phoneNumber;
    if (phoneNumberLength < 6) return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2)}`;
    if (phoneNumberLength < 10) return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 5)}-${phoneNumber.slice(5)}`;
    return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 6)}-${phoneNumber.slice(6, 10)}`;
  } else {
    // 010, 031, 070 등 3자리 국번 처리
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    if (phoneNumberLength < 11) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 7)}-${phoneNumber.slice(7, 11)}`;
  }
};


// 메인 ContactPage 컴포넌트
const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // useNaverMaps 훅을 사용하여 스크립트 준비 상태와 오류를 가져옴
  const { isReady, error: scriptError } = useNaverMaps();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (!name.trim() || !phone.trim() || !message.trim()) {
      alert("이름, 연락처, 문의 내용은 필수입니다.");
      setIsSubmitting(false);
      return;
    }

    // 연락처 유효성 검사 정규식 (한국 전화번호 형식 검증: 010/011/016/017/018/019/02/031~064/070/080 등 및 7~8자리 국번 지원)
    const phoneRegex = /^(01[016789]|02|0[3-9]{1}[0-9]{1})-[0-9]{3,4}-[0-9]{4}$/;
    if (!phoneRegex.test(phone)) {
      alert("올바른 연락처 형식이 아닙니다. (예: 010-1234-5678 또는 02-123-4567)");
      setIsSubmitting(false);
      return;
    }

    try {
      const inquiryData = {
        name: name, email: email, phone: phone, message: message,
        submittedAt: serverTimestamp(), status: "신규"
      };
      const inquiryDocRef = await addDoc(collection(db, "inquiries"), inquiryData);

      try {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 90);

        await addDoc(collection(db, "activities"), {
          type: "new_inquiry", text: `새 문의: "${name}"님의 문의 접수`,
          timestamp: serverTimestamp(), relatedId: inquiryDocRef.id,
          expireAt: expireDate,
          details: { inquiryName: name, inquiryEmail: email }
        });
      } catch (activityError) {
        console.error("Error logging activity: ", activityError);
      }

      setSubmitMessage("문의가 성공적으로 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.");
      setName(''); setEmail(''); setPhone(''); setMessage('');
    } catch (error) {
      console.error("Error adding inquiry document: ", error);
      setSubmitMessage("문의 접수 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyAddress = "강원특별자치도 원주시 문화촌길 81";

  return (
    <>
      <title>문의하기 - 진양건설</title>
      <div className="container mx-auto px-6 py-16 md:py-24">
        <AnimatedSection animationType="fade-in">
          <header className="text-center pt-8 pb-12 rounded-lg mb-16">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white tracking-tight">문의하기</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">시공 및 견적 등 궁금한 점이 있으시면 언제든지 연락주십시오.</p>
          </header>
        </AnimatedSection>
        <div className="space-y-16">
          <AnimatedSection className="grid lg:grid-cols-5 gap-12 bg-white dark:bg-slate-800 p-8 md:p-12 rounded-xl shadow-2xl" animationType="scale-up">
            <div className="lg:col-span-3">
              <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-white">온라인 문의 양식</h2>
              {submitMessage && (
                <div className={`p-4 mb-4 text-sm rounded-lg ${submitMessage.includes('성공') ? 'bg-green-100 dark:bg-green-700 text-green-700 dark:text-green-100' : 'bg-red-100 dark:bg-red-700 text-red-700 dark:text-red-100'}`} role="alert">
                  {submitMessage}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">성함</label>
                  <input type="text" id="name" name="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isSubmitting} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">이메일 주소 (선택)</label>
                  <input type="email" id="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isSubmitting} />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">연락처</label>
                  <input type="tel" id="phone" name="phone" required value={phone} onChange={(e) => setPhone(formatPhoneNumber(e.target.value))} placeholder="010-1234-5678" className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isSubmitting} />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">문의 내용</label>
                  <textarea id="message" name="message" rows="6" required value={message} onChange={(e) => setMessage(e.target.value)} placeholder="문의하실 내용을 자세히 적어주세요." className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isSubmitting}></textarea>
                </div>
                <button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-bold py-3.5 px-6 rounded-lg transition-all duration-300 text-lg shadow-md hover:shadow-lg disabled:opacity-70" disabled={isSubmitting}>
                  {isSubmitting ? '제출 중...' : '문의 보내기'}
                </button>
              </form>
            </div>
            <div className="lg:col-span-2 space-y-8 mt-8 lg:mt-0">
              <div className="bg-slate-50 dark:bg-slate-700/50 p-6 rounded-lg">
                <div className="flex items-center mb-6 pb-6 border-b border-gray-200 dark:border-slate-700">
                  <img src={logo} alt="진양건설 로고" className="h-16 w-16 mr-4 rounded-md object-contain bg-white p-1 shadow-sm" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white">진양건설</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">지붕 공사 전문 기업</p>
                  </div>
                </div>
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">본사 연락처</h2>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  <li><strong>주소:</strong> 강원특별자치도 원주시 문화촌길 81 진양건설</li>
                  <li><strong>대표전화:</strong> 033-748-1873</li>
                  <li><strong>이메일:</strong> jy1873@naver.com</li>
                  <li><strong>업무시간:</strong> 평일 09:00 - 18:00</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection animationType="fade-in">
            <div className="bg-white dark:bg-slate-800 p-8 md:p-12 rounded-xl shadow-2xl">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">찾아오시는 길</h2>
              {/* 스크립트가 로드되었을 때만 NaverMapComponent를 렌더링 */}
              {scriptError && <div className="text-red-500 text-center">{scriptError.message}</div>}
              {isReady ? (
                <NaverMapComponent address={companyAddress} />
              ) : (
                <div 
                  style={{ width: '100%', height: '450px' }}
                  className="rounded-lg overflow-hidden shadow-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500"
                >
                  지도 API 로딩 중...
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </>
  );
}
export default ContactPage;